/**
 * Tank gauging panel — one tank's reading, or the whole site's inventory.
 *
 * The design rule this screen follows: never show a number without what it rests on. Every
 * reading carries its measured error bar, its refusal reason, and the two pixel
 * measurements it came from, because the operator has no other way to tell a good fit from
 * a confident wrong one.
 *
 * Fill percentage and volume are NOT presented as equals, and the layout says so before the
 * text does. Fill is measured — the ratio w/L cancels the sun angle exactly and needs no
 * assumption at all. Volume additionally needs the tank's shell height, which comes from the
 * imagery date plus a standard satellite overpass time, so it sits in its own card, in the
 * warning colour, with the assumption written next to it. Same measured-vs-modelled split
 * camera_calibration draws.
 *
 * Three things here are load-bearing rather than decorative:
 *
 *  - `TankCrossSection` draws the tank with its roof where the measurement put it. Nobody
 *    who has not met shadow gauging before believes a bare percentage; the picture is what
 *    makes the claim inspectable.
 *  - A refusal gets more space than a success, not less. It carries the backend's reason
 *    verbatim plus `refusalHint()`, which answers the different question the operator is
 *    actually asking: what do I do about it.
 *  - The rollup publishes the sun cross-check. Fill needs no sun angle; volume rests
 *    entirely on the overpass assumption, and the agreement between the measured and
 *    computed shadow azimuths is the only thing that makes that assumption checkable.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Icon } from '../../icons';
import {
  BARRELS_PER_M3, BatchResult, ClassConsistency, Consistency, RoofType, SceneInfo,
  TankCandidate, TankReading,
  fillColor, fillLabel, gaugeAll, gaugeTank, getCandidates, refusalHint, setRoofType,
} from '../../data/tankGauge';
import TankCrossSection from './TankCrossSection';
import './tank.css';

interface TankGaugePanelProps {
  scope: string;
  /** Open on one tank, or omit for the site rollup. */
  polygonId?: number;
  polygonName?: string;
  onClose: () => void;
  /** Lets the map draw the measurement that produced the number on screen. */
  onReading?: (reading: TankReading | null) => void;
  /** Every reading from a site run, so the map layer can colour the whole farm at once. */
  onBatch?: (readings: TankReading[]) => void;
  onFlyTo?: (lat: number, lng: number) => void;
}

const ROOF_LABELS: Record<RoofType, string> = {
  external_floating: 'Εξωτερική πλωτή οροφή (external floating)',
  fixed: 'Σταθερή οροφή (fixed) — δεν μετριέται',
  unknown: 'Άγνωστη',
};

/** Fill bands, matching `fillColor` so the legend cannot drift from the map. */
const BANDS: { label: string; colour: string; test: (f: number) => boolean }[] = [
  { label: 'κάτω από 25%', colour: '#ef4444', test: (f) => f < 0.25 },
  { label: '25–50%', colour: '#f97316', test: (f) => f >= 0.25 && f < 0.5 },
  { label: '50–75%', colour: '#eab308', test: (f) => f >= 0.5 && f < 0.75 },
  { label: 'πάνω από 75%', colour: '#22c55e', test: (f) => f >= 0.75 },
];

function m3(value: number | null | undefined): string {
  return value === null || value === undefined
    ? '—' : `${Math.round(value).toLocaleString('el-GR')} m³`;
}

function bbl(value: number | null | undefined): string {
  return value === null || value === undefined
    ? '—' : `${Math.round(value).toLocaleString('el-GR')} βαρέλια`;
}

function pct(value: number | null | undefined): string {
  return value === null || value === undefined ? '—' : `${(value * 100).toFixed(0)}%`;
}

/** What the method is and why the number can be trusted, in two sentences. */
function MethodExplainer() {
  return (
    <div className="tank-explain">
      <details>
        <summary>Πώς μετριέται η στάθμη από δορυφόρο</summary>
        <div>
          Οι δεξαμενές με <strong>εξωτερική πλωτή οροφή</strong> είναι ανοιχτές από πάνω: η
          οροφή επιπλέει πάνω στο υγρό, οπότε όσο αδειάζει η δεξαμενή τόσο κατεβαίνει. Το
          τοίχωμα προς τον ήλιο ρίχνει τότε μια <strong>ημισέληνο σκιάς πάνω στην οροφή</strong>,
          με πλάτος <em>w</em>. Στην ίδια εικόνα, όλο το κέλυφος ρίχνει μια
          <strong> σκιά στο έδαφος</strong>, μήκους <em>L</em>.
          <div className="tank-formula">πληρότητα = 1 − w / L</div>
          <div style={{ marginTop: 8 }}>
            Διαιρώντας τα δύο, η γωνία του ήλιου <strong>απαλείφεται πλήρως</strong> — δεν
            χρειάζεται ούτε ώρα λήψης ούτε ύψος δεξαμενής. Γι’ αυτό η πληρότητα είναι
            μετρημένο μέγεθος. Ο <strong>όγκος</strong> χρειάζεται επιπλέον το ύψος κελύφους,
            που προκύπτει από την ημερομηνία λήψης, και γι’ αυτό δηλώνεται χωριστά ως
            υπολογισμένος.
          </div>
        </div>
      </details>
    </div>
  );
}

function SceneLine({ scene, sun }: { scene: SceneInfo | null; sun?: BatchResult['sun'] }) {
  if (!scene) return null;
  return (
    <div className="tank-scene">
      <div>
        Εικόνα: <b>{scene.source || '—'}{scene.product ? ` / ${scene.product}` : ''}</b>
        {scene.scene_date && <> · λήψη <b>{scene.scene_date}</b></>}
        {scene.native_res_m && <> · ανάλυση <b>{scene.native_res_m} m</b></>}
      </div>
      {sun && sun.azimuth_measured_deg !== null && (
        <div style={{ marginTop: 4 }}>
          Ήλιος: αζιμούθιο σκιάς <b>{sun.azimuth_measured_deg}°</b> μετρημένο από την εικόνα
          {sun.azimuth_computed_deg !== null && <> έναντι <b>{sun.azimuth_computed_deg}°</b> υπολογισμένου</>}
          {sun.azimuth_agreement_deg !== null && (
            <> · απόκλιση <b>{sun.azimuth_agreement_deg}°</b></>
          )}
          {sun.elevation_deg !== null && <> · ύψος ήλιου <b>{sun.elevation_deg}°</b></>}
        </div>
      )}
      <div style={{ marginTop: 4, opacity: 0.75 }}>{scene.attribution}</div>
    </div>
  );
}

/**
 * A refusal, with what to do about it.
 *
 * Given more room than a success on purpose. The backend's reason is shown verbatim — it is
 * the only thing that tells an operator whether to declare a roof type, run the site batch,
 * or accept that this tank cannot be read from this scene at all.
 */
function RefusalCard({ reading }: { reading: TankReading }) {
  const hint = refusalHint(reading.reason);
  return (
    <div className="tank-refusal">
      <div className="tank-refusal-title">Δεν μπορεί να μετρηθεί</div>
      <div className="tank-refusal-reason">{reading.reason}</div>
      {hint && <div className="tank-refusal-hint">{hint}</div>}
    </div>
  );
}

function ReadingCard({ reading, scene }: { reading: TankReading; scene: SceneInfo | null }) {
  if (!reading.ok) return <RefusalCard reading={reading} />;

  const fill = reading.fill_fraction ?? 0;
  const colour = fillColor(fill);
  const atLimit = Boolean(reading.at_resolution_limit);

  return (
    <div>
      <div className="tank-headline">
        <div>
          <div className="tank-figure" style={{ color: colour }}>
            {atLimit ? '≥' : ''}{(fill * 100).toFixed(0)}%
            {!atLimit && reading.fill_sigma && (
              <span className="tank-sigma">± {(reading.fill_sigma * 100).toFixed(1)}%</span>
            )}
          </div>
          <div className="tank-figure-unit">πληρότητα · μετρημένη</div>
        </div>
        <TankCrossSection
          fill={fill}
          sunElevationDeg={reading.sun_elevation_deg}
          crescentPx={reading.crescent_px}
          shadowPx={reading.shadow_px}
        />
      </div>

      <div className="tank-badges">
        <span className={`tank-badge ${reading.method === 'ratio' ? 'ok' : 'info'}`}>
          {reading.method === 'ratio'
            ? 'λόγος w/L — χωρίς παραδοχή για τον ήλιο'
            : 'ύψος από όμοιες δεξαμενές'}
        </span>
        {atLimit && (
          <span className="tank-badge warn">
            ημισέληνος κάτω από το όριο ανάλυσης — κατώτατο όριο, όχι ακριβής τιμή
          </span>
        )}
        {reading.cross_check_fill !== null && reading.cross_check_fill !== undefined && (
          <span className="tank-badge muted">
            δεύτερη εκτίμηση {pct(reading.cross_check_fill)}
          </span>
        )}
      </div>

      <div className="tank-split">
        <div className="tank-card measured">
          <div className="tank-card-tag">Μετρημένο</div>
          <div className="tank-card-value">
            {fillLabel(reading)}
          </div>
          <div className="tank-card-sub">
            Από w = {reading.crescent_px?.toFixed(1)} px και L = {reading.shadow_px?.toFixed(1)} px
            στην ίδια εικόνα. Καμία παραδοχή για τη γωνία του ήλιου.
          </div>
        </div>
        <div className="tank-card derived">
          <div className="tank-card-tag">Υπολογισμένο</div>
          <div className="tank-card-value">{m3(reading.volume_m3)}</div>
          <div className="tank-card-sub">
            {bbl(reading.volume_bbl)}. Προϋποθέτει ύψος κελύφους{' '}
            {reading.height_m?.toFixed(1) ?? '—'} m, από τη λήψη
            {scene?.scene_date ? ` ${scene.scene_date}` : ''} και τυπική ώρα διέλευσης ~10:30.
          </div>
        </div>
      </div>

      {(reading.warnings || []).length > 0 && (
        <div className="tank-badges">
          {reading.warnings!.map((w) => (
            <span key={w} className="tank-badge warn">⚠ {w}</span>
          ))}
        </div>
      )}

      {/* The raw pixel measurements. Kept visible on purpose: they are what the number was
          derived from, and a crescent of 2 px is a different claim from one of 40. */}
      <details className="tank-explain" style={{ marginTop: 12 }}>
        <summary>Αναλυτικά νούμερα μέτρησης</summary>
        <div className="tank-rows">
          <div className="tank-row"><span>Ημισέληνος w</span><span>{reading.crescent_px?.toFixed(2)} px</span></div>
          <div className="tank-row"><span>Σκιά εδάφους L</span><span>{reading.shadow_px?.toFixed(2)} px</span></div>
          <div className="tank-row"><span>Βάθος οροφής d</span><span>{reading.depth_m?.toFixed(1) ?? '—'} m</span></div>
          <div className="tank-row"><span>Ύψος κελύφους H</span><span>{reading.height_m?.toFixed(1) ?? '—'} m</span></div>
          <div className="tank-row"><span>Ακτίνα</span><span>{reading.radius_m?.toFixed(1) ?? '—'} m</span></div>
          <div className="tank-row"><span>Αζιμούθιο σκιάς</span><span>{reading.sun_azimuth_deg?.toFixed(0)}°</span></div>
          <div className="tank-row"><span>Ύψος ήλιου</span><span>
            {reading.sun_elevation_deg ? `${reading.sun_elevation_deg.toFixed(0)}°` : '—'}
          </span></div>
          <div className="tank-row"><span>Αβεβαιότητα πληρότητας</span><span>
            {reading.fill_sigma ? `± ${(reading.fill_sigma * 100).toFixed(1)}%` : '—'}
          </span></div>
        </div>
      </details>
    </div>
  );
}

/** The per-class error bars, which is where the honest uncertainty actually lives. */
function ClassTable({ classes }: { classes: ClassConsistency[] }) {
  const shown = classes.filter((c) => c.n > 0);
  if (!shown.length) return null;
  return (
    <table className="tank-classes">
      <thead>
        <tr>
          <th>Κατηγορία</th><th>Δεξαμενές</th><th>Ύψος κελύφους</th><th>Διασπορά</th>
        </tr>
      </thead>
      <tbody>
        {shown.map((c) => (
          <tr key={c.radius_m}>
            <td>R ≈ {c.radius_m} m</td>
            <td>{c.n} / {c.tanks}</td>
            <td>{c.median_m ? `${c.median_m.toFixed(1)} m` : '—'}</td>
            <td style={{ color: c.spread === null ? 'var(--color-text-muted)'
              : c.usable ? '#86efac' : '#fca5a5' }}>
              {c.spread === null ? '—' : `± ${(c.spread * 100).toFixed(1)}%`}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function ErrorBar({ consistency }: { consistency: Consistency | null }) {
  if (!consistency || consistency.spread === null) return null;
  return (
    <div className="tank-badges">
      <span className={`tank-badge ${consistency.usable ? 'ok' : 'warn'}`}>
        {/* This is the honest uncertainty: identically built tanks must share a shell
            height, so the spread across them IS the measurement error. Nothing modelled. */}
        ± {(consistency.spread * 100).toFixed(1)}% — από τη διασπορά ύψους σε{' '}
        {consistency.n} όμοιες δεξαμενές
        {!consistency.usable && ' · πολύ μεγάλη διασπορά, μη αξιόπιστο'}
      </span>
    </div>
  );
}

export default function TankGaugePanel({
  scope, polygonId, polygonName, onClose, onReading, onBatch, onFlyTo,
}: TankGaugePanelProps) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reading, setReading] = useState<TankReading | null>(null);
  const [scene, setScene] = useState<SceneInfo | null>(null);
  const [roof, setRoof] = useState<RoofType>('unknown');
  const [candidates, setCandidates] = useState<TankCandidate[] | null>(null);
  const [batch, setBatch] = useState<BatchResult | null>(null);
  const [tab, setTab] = useState<'gauged' | 'refused'>('gauged');

  const single = polygonId !== undefined;

  // Loaded in BOTH modes. It used to be skipped for a single tank, which meant the roof
  // type selector always opened on 'unknown' even for a tank whose type had been declared
  // earlier — and declaring the roof type is the one thing that resolves the full-vs-fixed
  // ambiguity, so silently forgetting it defeated the feature's main safeguard.
  useEffect(() => {
    let cancelled = false;
    getCandidates(scope)
      .then((r) => { if (!cancelled) setCandidates(r.tanks); })
      .catch((e) => { if (!cancelled) setError(e.message); });
    return () => { cancelled = true; };
  }, [scope]);

  useEffect(() => {
    if (!single || !candidates) return;
    const found = candidates.find((c) => c.polygon_id === polygonId);
    if (found) {
      setRoof(found.roof_type);
      if (found.last_reading && !reading) setReading(found.last_reading);
    }
    // `reading` is deliberately not a dependency: this seeds the panel with the stored
    // reading once, and must not overwrite a fresh measurement the operator just took.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [single, candidates, polygonId]);

  const runOne = useCallback(async () => {
    if (polygonId === undefined) return;
    setBusy(true); setError(null);
    try {
      const result = await gaugeTank(polygonId, scope);
      setReading(result);
      setScene(result.scene ?? null);
      onReading?.(result);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }, [polygonId, scope, onReading]);

  const runAll = useCallback(async () => {
    setBusy(true); setError(null);
    try {
      const result = await gaugeAll(scope);
      setBatch(result);
      setScene(result.scene);
      onBatch?.(result.tanks);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }, [scope, onBatch]);

  const changeRoof = useCallback(async (value: RoofType) => {
    if (polygonId === undefined) return;
    setRoof(value);
    try {
      await setRoofType(polygonId, value, scope);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }, [polygonId, scope]);

  /** Rows for the rollup list: measured tanks first, refusals behind their own tab. */
  const rows = useMemo(() => {
    // `tank_readings` has no name column, so a stored reading arrives nameless and would
    // list as '#412'. The candidate list is where the names live; graft them on.
    const named = new Map((candidates || []).map((c) => [c.polygon_id, c.name]));
    const source: TankReading[] = (batch
      ? batch.tanks
      : (candidates || [])
        .filter((c) => c.gaugeable)
        .map((c) => c.last_reading
          ?? { polygon_id: c.polygon_id, ok: false, reason: 'δεν έχει μετρηθεί ακόμη' })
    ).map((t) => (t.name ? t : { ...t, name: named.get(t.polygon_id) }));
    const gauged = source.filter((t) => t.ok)
      .sort((a, b) => (b.fill_fraction ?? 0) - (a.fill_fraction ?? 0));
    const refused = source.filter((t) => !t.ok);
    return { gauged, refused };
  }, [batch, candidates]);

  /**
   * The inventory as it stands, whether or not a batch has run in this session.
   *
   * Readings are durable, so a panel that showed nothing until the operator pressed
   * 'measure everything' was hiding numbers it already had — and the first thing anyone
   * opening a screen called 'Tank Inventory' wants is the inventory. The error bar comes
   * from `class_spread`, which each reading stores from the run that produced it, so the
   * figure is never published without one.
   */
  const stored = useMemo(() => {
    if (batch?.inventory || !rows.gauged.length) return null;
    const volume = rows.gauged.reduce((a, t) => a + (t.volume_m3 ?? 0), 0);
    const spreads = rows.gauged
      .map((t) => t.class_spread)
      .filter((v): v is number => typeof v === 'number' && v > 0)
      .sort((a, b) => a - b);
    const dates = rows.gauged
      .map((t) => t.measured_at).filter((d): d is string => Boolean(d)).sort();
    return {
      volume,
      tanks: rows.gauged.length,
      spread: spreads.length ? spreads[Math.floor(spreads.length / 2)] : null,
      measuredAt: dates.length ? dates[dates.length - 1].slice(0, 10) : null,
    };
  }, [batch, rows.gauged]);

  const distribution = useMemo(() => {
    const counts = BANDS.map((b) => rows.gauged.filter(
      (t) => b.test(t.fill_fraction ?? 0)).length);
    const total = counts.reduce((a, b) => a + b, 0);
    return { counts, total };
  }, [rows.gauged]);

  const flyToTank = useCallback((polygon: number) => {
    const found = candidates?.find((c) => c.polygon_id === polygon);
    if (found && onFlyTo) onFlyTo(found.lat, found.lng);
  }, [candidates, onFlyTo]);

  const title = single
    ? `Στάθμη δεξαμενής${polygonName ? ` — ${polygonName}` : ''}`
    : 'Απόθεμα εγκατάστασης — στάθμη από δορυφόρο';

  return (
    <div className="detail-overlay" onClick={onClose}>
      <div className="detail-panel tank-panel" onClick={(e) => e.stopPropagation()}>
        {/* `panel-header` + `detail-close-btn` is what every other detail panel uses;
            the pair this started with had no matching CSS and dropped the close button
            under the title. */}
        <div className="panel-header">
          <h3><Icon name="factory" /> {title}</h3>
          <button className="detail-close-btn" onClick={onClose} aria-label="Close"><Icon name="clear" /></button>
        </div>

        <div className="detail-content">
          {error && (
            <div className="tank-refusal" style={{ marginBottom: 10 }}>
              <div className="tank-refusal-title">Σφάλμα</div>
              <div className="tank-refusal-reason">{error}</div>
            </div>
          )}

          <MethodExplainer />

          {single ? (
            <>
              <label style={{ fontSize: 12, display: 'block', marginBottom: 10 }}>
                Τύπος οροφής
                <select value={roof} onChange={(e) => changeRoof(e.target.value as RoofType)}
                        style={{ width: '100%', marginTop: 4 }}>
                  {(Object.keys(ROOF_LABELS) as RoofType[]).map((k) => (
                    <option key={k} value={k}>{ROOF_LABELS[k]}</option>
                  ))}
                </select>
                {/* The one ambiguity the imagery cannot resolve: a brim-full floating roof
                    and a fixed roof both show no crescent. Until this is declared, a tank
                    with no visible crescent is refused rather than reported as full. */}
                <span style={{ fontSize: 11, opacity: 0.65, display: 'block', marginTop: 4 }}>
                  Μια γεμάτη πλωτή οροφή και μια σταθερή οροφή φαίνονται ίδιες από δορυφόρο —
                  γι’ αυτό μια δεξαμενή χωρίς ημισέληνο δεν αναφέρεται ως γεμάτη αν δεν το
                  δηλώσετε εδώ.
                </span>
              </label>

              <button className="primary-btn" onClick={runOne} disabled={busy}
                      style={{ width: '100%' }}>
                {busy ? 'Μέτρηση…' : 'Μέτρηση από δορυφόρο'}
              </button>

              {reading && <div style={{ marginTop: 14 }}>
                <ReadingCard reading={reading} scene={scene} />
              </div>}
              <SceneLine scene={scene} />
            </>
          ) : (
            <>
              <button className="primary-btn" onClick={runAll} disabled={busy}
                      style={{ width: '100%' }}>
                {busy ? 'Μέτρηση όλων…' : 'Μέτρηση όλων των δεξαμενών'}
              </button>
              {busy && (
                <div className="tank-empty">
                  Κατεβαίνουν δορυφορικά πλακίδια και μετριέται κάθε δεξαμενή. Την πρώτη φορά
                  διαρκεί μερικά λεπτά· μετά οι εικόνες είναι στην cache.
                </div>
              )}

              {stored && (
                <div className="tank-rollup-total" style={{ marginTop: 14 }}>
                  <div className="tank-card-tag" style={{ color: 'var(--color-accent)' }}>
                    Εκτιμώμενο απόθεμα · από τις τελευταίες μετρήσεις
                  </div>
                  <div className="tank-rollup-figure">{m3(stored.volume)}</div>
                  <div className="tank-rollup-sub">
                    {bbl(stored.volume * BARRELS_PER_M3)} · {stored.tanks} δεξαμενές
                    {stored.measuredAt && ` · μέτρηση ${stored.measuredAt}`}
                  </div>
                  {stored.spread !== null && (
                    <div className="tank-badges">
                      <span className="tank-badge ok">
                        ± {(stored.spread * 100).toFixed(1)}% — από τη διασπορά ύψους σε
                        {' '}όμοιες δεξαμενές
                      </span>
                    </div>
                  )}
                  <div className="tank-rollup-sub" style={{ marginTop: 8 }}>
                    Πατήστε «Μέτρηση όλων των δεξαμενών» για νέα μέτρηση σε όλη την
                    εγκατάσταση.
                  </div>
                </div>
              )}

              {batch?.inventory && (
                <>
                  <div className="tank-rollup-total" style={{ marginTop: 14 }}>
                    <div className="tank-card-tag" style={{ color: 'var(--color-accent)' }}>
                      Εκτιμώμενο απόθεμα
                    </div>
                    <div className="tank-rollup-figure">{m3(batch.inventory.volume_m3)}</div>
                    <div className="tank-rollup-sub">
                      {bbl(batch.inventory.volume_bbl)} · {batch.gauged} από{' '}
                      {batch.gauged + batch.refused} δεξαμενές μετρήθηκαν
                    </div>
                    <ErrorBar consistency={batch.consistency} />
                    {batch.sun?.agrees === false && (
                      <div className="tank-badges">
                        <span className="tank-badge warn">
                          Ο μετρημένος άξονας σκιάς διαφωνεί με τον υπολογισμένο ήλιο — οι
                          όγκοι δεν είναι αξιόπιστοι, οι πληρότητες παραμένουν
                        </span>
                      </div>
                    )}
                    {batch.dropped_over_limit > 0 && (
                      <div className="tank-rollup-sub">
                        {batch.dropped_over_limit} δεξαμενές δεν μετρήθηκαν (όριο παρτίδας).
                      </div>
                    )}
                  </div>

                  {batch.consistency?.classes && (
                    <>
                      <div className="tank-section-head">
                        Αβεβαιότητα ανά κατηγορία δεξαμενής
                      </div>
                      {/* The error bar is only an error bar within one build: identical
                          tanks must share a shell height, so their spread IS measurement
                          error. Pooling sizes would mostly measure that they are different
                          tanks. */}
                      <ClassTable classes={batch.consistency.classes} />
                    </>
                  )}
                </>
              )}

              {/* Outside the batch block on purpose: the spread of levels across the farm
                  is just as readable from stored readings as from a run taken seconds ago,
                  and it is the fastest way to see a site at a glance. */}
              {distribution.total > 0 && (
                <>
                  <div className="tank-dist">
                    {BANDS.map((b, i) => distribution.counts[i] > 0 && (
                      <div key={b.label} style={{
                        width: `${(distribution.counts[i] / distribution.total) * 100}%`,
                        background: b.colour,
                      }} />
                    ))}
                  </div>
                  <div className="tank-legend">
                    {BANDS.map((b, i) => (
                      <span key={b.label}>
                        <i style={{ background: b.colour }} />
                        {b.label}: {distribution.counts[i]}
                      </span>
                    ))}
                  </div>
                </>
              )}

              {batch?.note && <div className="tank-empty">{batch.note}</div>}

              {(rows.gauged.length > 0 || rows.refused.length > 0) && (
                <>
                  <div className="tank-tabs" style={{ marginTop: 16 }}>
                    <button className={`tank-tab ${tab === 'gauged' ? 'active' : ''}`}
                            onClick={() => setTab('gauged')}>
                      Μετρημένες ({rows.gauged.length})
                    </button>
                    <button className={`tank-tab ${tab === 'refused' ? 'active' : ''}`}
                            onClick={() => setTab('refused')}>
                      Χωρίς μέτρηση ({rows.refused.length})
                    </button>
                  </div>

                  <div className="tank-list">
                    {(tab === 'gauged' ? rows.gauged : rows.refused).map((t) => {
                      const fill = t.fill_fraction ?? 0;
                      return (
                        <div key={t.polygon_id}
                             className={`tank-item ${t.ok ? '' : 'refused'}`}
                             onClick={() => flyToTank(t.polygon_id)}
                             title={t.ok ? (t.warnings || []).join(' · ') : t.reason}>
                          <span className="tank-item-dot"
                                style={{ background: t.ok ? fillColor(fill) : '#4b5563' }} />
                          <span className="tank-item-name">{t.name || `#${t.polygon_id}`}</span>
                          {t.ok ? (
                            <span className="tank-item-bar">
                              <div style={{ width: `${fill * 100}%`, background: fillColor(fill) }} />
                            </span>
                          ) : <span />}
                          <span className="tank-item-pct">
                            {t.ok ? fillLabel(t) : '—'}
                          </span>
                        </div>
                      );
                    })}
                    {tab === 'refused' && rows.refused.length === 0 && (
                      <div className="tank-empty">Καμία δεξαμενή δεν απορρίφθηκε.</div>
                    )}
                    {tab === 'gauged' && rows.gauged.length === 0 && (
                      <div className="tank-empty">
                        Δεν υπάρχουν ακόμη μετρήσεις. Πατήστε «Μέτρηση όλων των δεξαμενών».
                      </div>
                    )}
                  </div>
                </>
              )}

              <SceneLine scene={scene} sun={batch?.sun} />
            </>
          )}
        </div>
      </div>
    </div>
  );
}
